"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CATEGORY_SECTION_ORDER = exports.DocumentBindingSchema = exports.BindingItemSchema = exports.bindingSubjectKey = exports.BindingSubjectSchema = exports.BINDING_SUBJECT_TYPES = void 0;
exports.categoryRank = categoryRank;
exports.resolveBindings = resolveBindings;
const zod_1 = require("zod");
const documents_1 = require("./documents");
const documentSigning_1 = require("./documentSigning");
const documentAcceptanceModes_1 = require("./documentAcceptanceModes");
/**
 * THE BINDING REGISTRY — which documents a thing being sold owes.
 *
 * One row per subject, so "what does a K10 owe?" is a GetItem rather than a
 * scan, and "what does this whole order owe?" is one GetItem per subject the
 * order names. The resolution below is deliberately PURE: it takes rows and
 * returns items, touches no database, and is therefore the same code the
 * management screen previews with and the binder builds from.
 *
 * THE GOVERNING RULE: no binding means no document. A device with nothing
 * bound contributes nothing and blocks nothing — the same "no record =
 * ungated" discipline the dispatch gate uses. That is what makes the registry
 * safe to populate incrementally.
 */
exports.BINDING_SUBJECT_TYPES = [
    'device_model', 'device_sku', 'financing_term', 'financing_policy',
    'software_package', 'subscription_plan', 'checkout', 'business_type', 'jurisdiction',
];
/** Lands in a DynamoDB sort key, so `#` and whitespace are excluded. */
const SubjectId = zod_1.z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/, 'subject id may contain letters, digits, underscore, dot and hyphen only');
exports.BindingSubjectSchema = zod_1.z.object({
    subjectType: zod_1.z.enum(exports.BINDING_SUBJECT_TYPES),
    subjectId: SubjectId,
});
const bindingSubjectKey = (s) => `${s.subjectType}#${s.subjectId}`;
exports.bindingSubjectKey = bindingSubjectKey;
exports.BindingItemSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(documents_1.DOCUMENT_KINDS),
    required: zod_1.z.boolean().default(true),
    signerRoles: zod_1.z.array(zod_1.z.enum(documentSigning_1.SIGNER_ROLES)).default([]),
    acceptanceMode: documentAcceptanceModes_1.AcceptanceModeSchema,
    /** Overrides the category rank when the operator wants a specific position. */
    sectionOrder: zod_1.z.number().int().positive().optional(),
}).refine((i) => (i.kind === 'policy' ? i.signerRoles.length === 0 : i.signerRoles.length > 0), {
    message: 'a policy is accepted, not signed (no signer roles); an agreement must name at least '
        + 'one signer role — a section addressed to nobody can never be discharged',
    path: ['signerRoles'],
});
exports.DocumentBindingSchema = zod_1.z.object({
    subjectType: zod_1.z.enum(exports.BINDING_SUBJECT_TYPES),
    subjectId: SubjectId,
    items: zod_1.z.array(exports.BindingItemSchema).max(40),
    note: zod_1.z.string().max(500).optional(),
    updatedAt: zod_1.z.string().min(1),
    updatedBy: zod_1.z.string().min(1),
}).refine((b) => new Set(b.items.map((i) => i.documentId)).size === b.items.length, {
    message: 'the same document is bound twice to this subject — merge the two entries, because '
        + 'two rows for one document would silently disagree about required/acceptanceMode',
    path: ['items'],
});
/**
 * READING ORDER for the binder. Device terms first because they are what the
 * customer came for; the tenant-level terms last because they are the frame
 * around everything else. An operator-created category has no opinion here and
 * sorts after every built-in, alphabetically among its peers.
 */
exports.CATEGORY_SECTION_ORDER = [
    'device_purchase', 'device_rental', 'device_finance',
    'checkout', 'payments',
    'software_license', 'subscription',
    'tenant', 'partner', 'employee', 'hr_policy', 'vendor', 'internal', 'security',
];
function categoryRank(category) {
    if (!category)
        return exports.CATEGORY_SECTION_ORDER.length + 1;
    const i = exports.CATEGORY_SECTION_ORDER.indexOf(category);
    return i === -1 ? exports.CATEGORY_SECTION_ORDER.length : i;
}
/** Higher wins when two bindings disagree. Strongest obligation survives. */
const MODE_STRENGTH = {
    acknowledge_once: 0,
    acknowledge_each_version: 1,
    sign_once: 2,
    sign_each_version: 3,
};
/**
 * Resolve every subject in a scenario into one ordered, deduped list.
 *
 * FOUR RULES, each with a test above:
 *
 *  1. A document bound by several subjects appears ONCE. A customer signing the
 *     same waiver three times because they bought three terminals is a defect.
 *  2. When bindings disagree, the STRONGEST obligation wins — required beats
 *     optional, and the higher acceptance mode wins. Merging downward would let
 *     adding a binding quietly weaken an existing one.
 *  3. Signer roles UNION. Two bindings each naming a different signer both meant
 *     it; dropping one would leave a section nobody signs.
 *  4. A retired or unknown document is DROPPED, not an error. Retiring a
 *     document must not break every checkout that referenced it — the whole
 *     point of retirement is that it stops creating new obligations.
 */
function resolveBindings(subjects, bindings, opts) {
    const wanted = new Set(subjects.map(exports.bindingSubjectKey));
    const merged = new Map();
    for (const binding of bindings) {
        const key = (0, exports.bindingSubjectKey)(binding);
        if (!wanted.has(key))
            continue;
        for (const item of binding.items) {
            const doc = opts.catalogue[item.documentId];
            if (!doc || doc.status === 'retired')
                continue; // rule 4
            const existing = merged.get(item.documentId);
            if (!existing) {
                merged.set(item.documentId, {
                    documentId: item.documentId,
                    title: doc.title,
                    kind: doc.kind,
                    category: doc.category,
                    required: item.required,
                    signerRoles: [...item.signerRoles],
                    acceptanceMode: item.acceptanceMode,
                    sectionOrder: item.sectionOrder,
                    boundBy: [key],
                });
                continue;
            }
            existing.boundBy.push(key); // rule 1
            existing.required = existing.required || item.required; // rule 2
            if (MODE_STRENGTH[item.acceptanceMode] > MODE_STRENGTH[existing.acceptanceMode]) {
                existing.acceptanceMode = item.acceptanceMode; // rule 2
            }
            for (const role of item.signerRoles) { // rule 3
                if (!existing.signerRoles.includes(role))
                    existing.signerRoles.push(role);
            }
            if (item.sectionOrder !== undefined) {
                existing.sectionOrder = existing.sectionOrder === undefined
                    ? item.sectionOrder
                    : Math.min(existing.sectionOrder, item.sectionOrder);
            }
        }
    }
    // Deterministic: explicit order first, then category rank, then documentId.
    // The final documentId tiebreak is what makes two runs over the same data
    // byte-identical regardless of the order subjects arrived in.
    return [...merged.values()].sort((a, b) => {
        const ao = a.sectionOrder ?? Number.MAX_SAFE_INTEGER;
        const bo = b.sectionOrder ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo)
            return ao - bo;
        const ar = categoryRank(a.category);
        const br = categoryRank(b.category);
        if (ar !== br)
            return ar - br;
        return a.documentId.localeCompare(b.documentId);
    }).map((item) => ({ ...item, boundBy: [...item.boundBy].sort() }));
}
//# sourceMappingURL=documentBindings.js.map